import React, { useState, useEffect } from "react";
import Mux from "@mux/mux-node";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { ElasticsearchDocument } from "./types/Elasticsearch";
import MapView from "./MapView";
import CustomDrawer from "./CustomDrawer";
import { useMapContext } from "./MapContext";
import { DrawerContentType, useDrawerContext } from "./DrawerContext";
import { Outlet } from "react-router-dom";

const MapDashboard = () => {
  const { setSelectedPoint, setSelectedDocument } = useMapContext();
  const [streamData, setStreamData] =
    useState<Mux.Video.LiveStreams.LiveStream | null>(null);
  const [documents, setDocuments] = useState<ElasticsearchDocument[]>([]);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const { drawerContentType, setDrawerContentType } = useDrawerContext();

  const handleMapRightClick = (event: {
    latlng: L.LatLng;
    containerPoint: { x: number; y: number };
  }) => {
    setSelectedPoint(event.latlng);
    setContextMenu({
      x: event.containerPoint.x,
      y: event.containerPoint.y,
    });
  };

  const handleMenuOptionClick = (contentType: DrawerContentType) => {
    setDrawerContentType(contentType);
    setContextMenu(null); // Hide the context menu
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DRONESHOT_MUX_LIVE_API_URL}/incidents`
      );
      setDocuments(response.data?.hits?.hits); // Assuming Elasticsearch response format
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  };

  // Fetch documents from Elasticsearch
  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleStartStream = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_DRONESHOT_MUX_LIVE_API_URL}/mux/start`
      );
      setStreamData(response.data);
    } catch (err) {
      console.error("Failed to start stream:", err);
      alert("Failed to start stream. Please try again.");
    }
  };

  const handleGetStream = async (streamId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DRONESHOT_MUX_LIVE_API_URL}/mux/live-streams/${streamId}`
      );
      setStreamData(response.data);
    } catch (err) {
      console.error("Failed to get stream:", err);
      alert("Failed to get stream. Please try again.");
    }
  };

  const handleCompleteStream = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_DRONESHOT_MUX_LIVE_API_URL}/mux/complete`,
        {
          streamId: streamData?.id,
        }
      );
    } catch (err) {
      console.error("Failed to complete stream:", err);
      alert("Failed to complete stream. Please try again.");
    } finally {
      setStreamData(null);
    }
  };

  const onDrawerClose = () => {
    setDrawerContentType(null);
    setSelectedPoint(null); // Reset selected point
    setSelectedDocument(null); // Reset selected document
  };

  return (
    <div style={{ position: "relative", height: "100vh" }}>
      {/* Renders child routes */}
      <Outlet />

      {/* Top horizontal bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          backgroundColor: "#f8f9fa",
          padding: "10px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          zIndex: 1000,
          textAlign: "center",
          fontSize: "14px",
          fontWeight: "bold",
          color: "#333",
        }}
      >
        Right-click on the map for more options.
      </div>

      <MapView
        documents={documents}
        onMapClick={() => {
          setContextMenu(null); // Hide the context menu
        }}
        onMapRightClick={handleMapRightClick}
      />

      {contextMenu && (
        <div
          style={{
            position: "absolute",
            top: contextMenu.y,
            left: contextMenu.x,
            backgroundColor: "white",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            borderRadius: "4px",
            zIndex: 1000,
          }}
        >
          <div
            style={{ padding: "8px", cursor: "pointer" }}
            onClick={() =>
              handleMenuOptionClick(DrawerContentType.REPORT_ISSUE)
            }
          >
            Report an Issue
          </div>
          <div
            style={{ padding: "8px", cursor: "pointer" }}
            onClick={() =>
              handleMenuOptionClick(DrawerContentType.ASK_QUESTION)
            }
          >
            Ask a Question
          </div>
        </div>
      )}

      <CustomDrawer
        open={drawerContentType !== null}
        onClose={onDrawerClose}
        onSuccess={fetchDocuments}
        streamData={streamData}
        onStartStream={handleStartStream}
        onGetStream={handleGetStream}
        onCompleteStream={handleCompleteStream}
      />
    </div>
  );
};

export default MapDashboard;
